/**
 * FreeformRubricAnalysis — schema view for `FreeformRubricV1`.
 * Composes:
 *   - StepsList             (overall stepsAnalysis)
 *   - InterpretationBlock   (approachSummary — admin audience only)
 *   - VisualChecksList      (when visual-analysis prompts emit it — admin only)
 *   - HintsList             (question-specific next step)
 *   - SuggestionsList       (general practice going forward)
 *
 * No chrome — the caller (post-attempt / during-attempt wrapper) handles
 * surrounding layout, density (collapsing via `<details>`), score header,
 * etc.
 */
import type { Feedback } from '@padcanvas/shared';
import { VerdictBlock } from '../sections/VerdictBlock';
import { DiagnosisBlock } from '../sections/DiagnosisBlock';
import { diagnosisForStudent } from '../sections/diagnosisLabels';
import { RationaleList } from '../sections/RationaleList';
import { GatedNote } from '../sections/GatedNote';
import { StepsList } from '../sections/StepsList';
import { InterpretationBlock } from '../sections/InterpretationBlock';
import { VisualChecksList } from '../sections/VisualChecksList';
import { SuggestionsList } from '../sections/SuggestionsList';
import { HintsList } from '../sections/HintsList';
import { AdminOnlySection } from '../sections/AdminOnlySection';
import type { SolutionAnalysisAudience } from '../audience';

interface Props {
    feedback: Feedback;
    audience?: SolutionAnalysisAudience;
    /** Show the tutor-feedback v2 surfaces (verdict, diagnosis, rationale).
     *  Defaults to shown; the student app passes the `analysis_view_v2` gate. */
    analysisV2?: boolean;
    /** Question type, for rationale suppression on choice questions
     *  (see RationaleList). Optional — unknown keeps every rationale. */
    questionType?: string | null;
}

export function FreeformRubricAnalysis({ feedback, audience = 'admin', analysisV2, questionType }: Props) {
    const a = feedback.llmAssessment;
    const showAdminOnly = audience === 'admin';
    // v2 surfaces show by default (admin/teacher review); the student app gates
    // them behind `analysis_view_v2`. Admins see them with a "not shown to
    // students" note when the gate would hide them from students.
    const showV2 = analysisV2 ?? true;
    return (
        <>
            {showV2 && (
                <>
                    {audience === 'admin' && <GatedNote gate="analysis_view_v2" />}
                    <VerdictBlock verdict={a?.verdict} />
                    <RationaleList
                        accuracyRationale={a?.accuracyRationale}
                        reasoningRationale={a?.reasoningRationale}
                        accuracyScore={feedback.computedScores?.correctnessScore}
                        reasoningScore={feedback.computedScores?.approachScore}
                        questionType={questionType}
                    />
                </>
            )}
            {/* Diagnosis (named cause + slip/gap) is admin-only — NOT shown to
                students. Admin/teacher review + the parsed operation/model-run
                views (audience 'admin') still render it. */}
            {/* Mounted only with a diagnosis to show: the heading is unconditional,
                and a withheld diagnosis (selection gate) would leave it bare. */}
            {showAdminOnly && diagnosisForStudent(a?.diagnosis) && (
                <AdminOnlySection title="Diagnosis">
                    <DiagnosisBlock diagnosis={a?.diagnosis} />
                </AdminOnlySection>
            )}
            <StepsList steps={a?.stepsAnalysis ?? []} title="Step analysis" collapsible richSteps={showV2} lowTrust={feedback.trust?.level === 'low'} />
            {showAdminOnly && a?.interpretation?.approachSummary && (
                <AdminOnlySection>
                    <InterpretationBlock interpretation={a.interpretation} />
                </AdminOnlySection>
            )}
            {showAdminOnly && a?.visualChecks && a.visualChecks.length > 0 && (
                <AdminOnlySection>
                    <VisualChecksList items={a.visualChecks} />
                </AdminOnlySection>
            )}
            <HintsList items={a?.solutionGuidance} />
            <SuggestionsList items={a?.suggestionsGeneral} />
        </>
    );
}
